import { useState, useEffect } from 'react';
import { ExternalLink, ArrowRight, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { Quote } from '../../services/types';
import { cn } from '../../lib/utils';

export function QuoteBrowser() {
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadQuotes();
    }, []);

    const loadQuotes = async () => {
        try {
            setLoading(true);
            const data = await api.external.listQuotes();
            setQuotes(data);
        } catch (error) {
            console.error("Failed to load quotes:", error);
            // Optionally set error state
        } finally {
            setLoading(false);
        }
    };

    const handleConvert = async (quoteId: string) => {
        try {
            await api.manufacturing.createWorkOrderFromQuote(quoteId);
            toast.success(`Quote ${quoteId} converted to Work Order successfully!`);
            loadQuotes();
        } catch (e: any) {
            toast.error(e.message || 'Failed to convert quote');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Connecting to Estimation DBMS...</div>;

    return (
        <div className="h-full flex flex-col space-y-6">
            <div className="flex justify-between items-center">
                <div className="space-y-1">
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">External Quotes</h2>
                    <p className="text-muted-foreground">Browsing approved estimates from the Estimation DBMS</p>
                </div>
                <button
                    onClick={loadQuotes}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                    Refresh Feed
                </button>
            </div>

            <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
                <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 text-slate-700 font-medium border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-3">Quote Ref</th>
                            <th className="px-6 py-3">Customer</th>
                            <th className="px-6 py-3">Date</th>
                            <th className="px-6 py-3 text-right">Value</th>
                            <th className="px-6 py-3">Status</th>
                            <th className="px-6 py-3 text-right">Integration</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {quotes.map((q) => (
                            <tr key={q.id} className="hover:bg-slate-50">
                                <td className="px-6 py-3 font-semibold text-blue-600 flex items-center gap-1">
                                    {q.id} <ExternalLink className="w-3 h-3" />
                                </td>
                                <td className="px-6 py-3">{q.customer_name}</td>
                                <td className="px-6 py-3 text-slate-500">{q.date}</td>
                                <td className="px-6 py-3 text-right font-medium">${q.total_amount.toLocaleString()}</td>
                                <td className="px-6 py-3">
                                    <span className={cn(
                                        "px-2 py-1 rounded-full text-xs font-semibold",
                                        q.status === 'Approved' ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                                    )}>
                                        {q.status}
                                    </span>
                                </td>
                                <td className="px-6 py-3 text-right">
                                    {q.status === 'Approved' ? (
                                        <button
                                            onClick={() => handleConvert(q.id)}
                                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 ml-auto"
                                        >
                                            Convert to WO <ArrowRight className="w-4 h-4" />
                                        </button>
                                    ) : (
                                        <span className="flex items-center gap-1 text-slate-400 ml-auto justify-end">
                                            <CheckCircle className="w-4 h-4" /> Linked
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
